import React, { useState, useEffect } from "react";
import { UseUserAuth } from "./context/UseAuthContext";
import PageNav from "./components/PageNav";
import { useLocation } from "react-router-dom";
import { CheckBadgeIcon, DocumentArrowDownIcon, PrinterIcon, XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon, InformationCircleIcon } from "@heroicons/react/24/outline";

// Modal Component
const Modal = ({ isOpen, onClose, type = 'info', title, message, showCloseButton = true }) => {
    if (!isOpen) return null;

    const getModalStyles = () => {
        switch (type) {
            case 'success':
                return {
                    iconColor: 'text-green-600',
                    bgColor: 'bg-green-50',
                    borderColor: 'border-green-200',
                    buttonColor: 'bg-green-600 hover:bg-green-700'
                };
            case 'error':
                return {
                    iconColor: 'text-red-600',
                    bgColor: 'bg-red-50',
                    borderColor: 'border-red-200',
                    buttonColor: 'bg-red-600 hover:bg-red-700'
                };
            case 'warning':
                return {
                    iconColor: 'text-yellow-600',
                    bgColor: 'bg-yellow-50',
                    borderColor: 'border-yellow-200',
                    buttonColor: 'bg-yellow-600 hover:bg-yellow-700'
                };
            default:
                return {
                    iconColor: 'text-blue-600',
                    bgColor: 'bg-blue-50',
                    borderColor: 'border-blue-200',
                    buttonColor: 'bg-blue-600 hover:bg-blue-700'
                };
        }
    };

    const styles = getModalStyles();

    const getIcon = () => {
        switch (type) {
            case 'success':
                return <CheckCircleIcon className={`h-6 w-6 ${styles.iconColor}`} />;
            case 'error':
                return <XCircleIcon className={`h-6 w-6 ${styles.iconColor}`} />;
            case 'warning':
                return <ExclamationTriangleIcon className={`h-6 w-6 ${styles.iconColor}`} />;
            case 'info':
                return <InformationCircleIcon className={`h-6 w-6 ${styles.iconColor}`} />;
            default:
                return <InformationCircleIcon className={`h-6 w-6 ${styles.iconColor}`} />;
        }
    };

    return (
        <div className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
                </div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div className={`${styles.bgColor} ${styles.borderColor} border-l-4 p-4`}>
                        <div className="flex">
                            <div className="flex-shrink-0">
                                {getIcon()}
                            </div>
                            <div className="ml-3 flex-1">
                                {title && (
                                    <h3 className="text-sm font-medium text-gray-900 mb-1">
                                        {title}
                                    </h3>
                                )}
                                <div className="text-sm text-gray-700">
                                    {message}
                                </div>
                            </div>
                            {showCloseButton && (
                                <div className="ml-auto pl-3">
                                    <div className="-mx-1.5 -my-1.5">
                                        <button
                                            onClick={onClose}
                                            className="inline-flex rounded-md p-1.5 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        >
                                            <XMarkIcon className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {showCloseButton && (
                        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                            <button
                                onClick={onClose}
                                className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 ${styles.buttonColor} text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm`}
                            >
                                OK
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};


export default function PrintBookOrderProcessing() {
    const { user } = UseUserAuth();
    const location = useLocation();
    const { storyId, storyTitle, orderId } = location.state || {};
    
    // State variables
    const [userName, setUserName] = useState("");
    const [storyImages, setStoryImages] = useState([]);
    const [imageData, setImageData] = useState([]); // Store both URL and imageNum
    const [loadingImages, setLoadingImages] = useState(false);
    const [selectedCoverImage, setSelectedCoverImage] = useState("");
    const [selectedBookId, setSelectedBookId] = useState("");
    const [coverImageConfirmed, setCoverImageConfirmed] = useState(false);
    const [generatingPDFs, setGeneratingPDFs] = useState(false); // Track PDF generation
    const [generatingCoverPDF, setGeneratingCoverPDF] = useState(false);
    const [printBookPdfGenerated, setPrintBookPdfGenerated] = useState(false);
    const [coverPdfGenerated, setCoverPdfGenerated] = useState(false);
    const [validationStatus, setValidationStatus] = useState('pending'); // 'pending', 'validating', 'ready', 'failed' ----absent in latest version
    const [validationDetails, setValidationDetails] = useState(null);//---absent in latest version
    const [canSubmitToPrint, setCanSubmitToPrint] = useState(false);//---absent in latest version
    const [submittingToPrint, setSubmittingToPrint] = useState(false);
    const [submissionSuccessful, setSubmissionSuccessful] = useState(false);
    const [userSubmissionSuccessful, setUserSubmissionSuccessful] = useState(false);


    // Modal state
    const [modal, setModal] = useState({
        isOpen: false,
        type: 'info',
        title: '',
        message: ''
    });

    // Helper function to show modal
    const showModal = (type, title, message) => {
        setModal({
            isOpen: true,
            type,
            title,
            message
        });
    };

    // Helper function to close modal
    const closeModal = () => {
        setModal(prev => ({ ...prev, isOpen: false }));
    };

    const fetchUserName = async () => {
        try {
            const idToken = await user.getIdToken();
            const response = await fetch(`${process.env.REACT_APP_BACKEND}/user/getUserName`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: idToken,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch user name");
            }

            const data = await response.json();
            setUserName(data.userName || data.name || data); // Handle different response formats
        } catch (error) {
            console.error("Error fetching user name:", error);
        }
    };

    // Function to check if cover image is already set
    const checkCoverImageStatus = async (storyID) => {
        try {
            const idToken = await user.getIdToken();
            const response = await fetch(`${process.env.REACT_APP_BACKEND}/user/getCoverImage`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: idToken,
                },
                body: JSON.stringify({ storyID: storyID })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.isSet && data.coverImageNum) {
                    setSelectedCoverImage(data.coverImageNum);
                    setCoverImageConfirmed(true);
                    console.log("Cover image already set:", data.coverImageNum);
                    // Pass the coverImageNum directly, don't rely on state
                    await fetchSelectedCoverImage(storyID, data.coverImageNum);
                    return true; // Cover already set
                }
            }
            return false; // Cover not set yet
        } catch (error) {
            console.error("Error checking cover image status:", error);
            return false;
        }
    };

    // Function to fetch only the selected cover image
    const fetchSelectedCoverImage = async (storyID, imageNum) => {
        console.log("fetchSelectedCoverImage called with:", { storyID, imageNum });
        if (!imageNum) {
            console.error("No imageNum provided to fetchSelectedCoverImage");
            return;
        }
        try {
            setLoadingImages(true);
            const idToken = await user.getIdToken();
            const bookId = `${storyID}_${imageNum}`;

            console.log("Fetching single image with bookId:", bookId);

            const response = await fetch(`${process.env.REACT_APP_BACKEND}/user/imageOutput`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: idToken,
                },
                body: JSON.stringify({
                    bookId: bookId,
                }),
            });

            if (response.ok) {
                const blob = await response.blob();
                const imageUrl = URL.createObjectURL(blob);

                const imageData = {
                    imageUrl: imageUrl,
                    imageNum: imageNum,
                    bookId: bookId,
                    index: 0
                };

                setImageData([imageData]);
                setStoryImages([imageUrl]);
                console.log("Fetched selected cover image", imageNum);
            }
        } catch (error) {
            console.error("Error fetching selected cover image:", error);
        } finally {
            setLoadingImages(false);
        }
    };

    // New function to fetch story images using existing imageOutput endpoint
    const fetchStoryImages = async (storyID) => {
        try {
            setLoadingImages(true);
            const idToken = await user.getIdToken();

            // First get the imageNum array from backend
            const imageNumResponse = await fetch(`${process.env.REACT_APP_BACKEND}/user/getImageNumbers`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: idToken,
                },
                body: JSON.stringify({ storyID: storyID })
            });

            if (!imageNumResponse.ok) {
                throw new Error("Failed to fetch image numbers");
            }

            const imageNumData = await imageNumResponse.json();
            const imageNumArray = imageNumData.imageNumbers; // Array like ['1', '2_12', '3_3', etc.]

            console.log("Image numbers:", imageNumArray);

            // Now fetch each image using the existing imageOutput endpoint
            const fetchedImages = [];
            const fetchedImageData = [];
            for (let i = 0; i < imageNumArray.length; i++) {
                try {
                    const imageNum = imageNumArray[i];
                    const bookId = `${storyID}_${imageNum}`;

                    const response = await fetch(`${process.env.REACT_APP_BACKEND}/user/imageOutput`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: idToken,
                        },
                        body: JSON.stringify({
                            bookId: bookId,
                        }),
                    });

                    if (response.ok) {
                        const blob = await response.blob();
                        const imageUrl = URL.createObjectURL(blob);
                        fetchedImages.push(imageUrl);
                        fetchedImageData.push({
                            imageUrl: imageUrl,
                            imageNum: imageNum,
                            bookId: bookId,
                            index: i
                        });
                    } else if (response.status === 404) {
                        console.warn(`Image not found for bookId: ${bookId}`);
                    } else {
                        console.error(`Failed to fetch image ${i + 1}:`, response.status);
                    }
                } catch (error) {
                    console.error(`Error fetching image ${i + 1}:`, error);
                }
            }

            setStoryImages(fetchedImages);
            setImageData(fetchedImageData);
            console.log("Fetched images:", fetchedImages.length);
            console.log("Image data:", fetchedImageData);
        } catch (error) {
            console.error("Error fetching story images:", error);
            setStoryImages([]);
            setImageData([]);
        } finally {
            setLoadingImages(false);
        }
    };

    // Handle cover image selection
    const handleCoverImageSelect = (imageData) => {
        setSelectedCoverImage(imageData.imageNum);
        setSelectedBookId(imageData.bookId);
        console.log("Selected cover image:", {
            imageNum: imageData.imageNum,
            bookId: imageData.bookId,
            index: imageData.index
        });
    };


const submitCoverImageSelection = async () => {
    if (!selectedBookId || !storyId) {
        showModal('error', 'Selection Required', 'Please select a cover image first');
        return;
    }

    try {
        setGeneratingPDFs(true);
        const idToken = await user.getIdToken();
        
        // Step 1: Save cover selection
        const coverResponse = await fetch(`${process.env.REACT_APP_BACKEND}/user/setCoverImage`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: idToken,
            },
            body: JSON.stringify({
                storyID: storyId,
                selectedImageNum: selectedCoverImage,
                selectedBookId: selectedBookId
            })
        });

        if (!coverResponse.ok) {
            throw new Error("Failed to save cover image selection");
        }
        console.log("Cover image selection saved successfully");
        // Clean up unselected images
        imageData.forEach(item => {
            if (item.imageNum !== selectedCoverImage && item.imageUrl.startsWith('blob:')) {
                URL.revokeObjectURL(item.imageUrl);
            }
        });

        const selectedImageData = imageData.find(item => item.imageNum === selectedCoverImage);
        if (selectedImageData) {
            setImageData([selectedImageData]);
            setStoryImages([selectedImageData.imageUrl]);
        }

        //setCoverImageConfirmed(true);

        // Step 2: Generate both PDFs automatically
        console.log("Generating both PDF files...");
        
        let printPdfSuccess = false;
        let coverPdfSuccess = false;
        let printPdfError = null;
        let coverPdfError = null;

        // Generate print PDF
        try {
            console.log("Generating print PDF...");
            const printResponse = await fetch(`${process.env.REACT_APP_BACKEND}/user/getPrintPdf`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: idToken,
                },
                body: JSON.stringify({ bookId: storyId })
            });

            if (printResponse.ok) {
                setPrintBookPdfGenerated(true);
                printPdfSuccess = true;
                console.log("Print PDF generated successfully");
            } else {
                const errorText = await printResponse.text();
                printPdfError = `Print PDF generation failed: ${errorText}`;
                console.error(printPdfError);
            }
        } catch (error) {
            printPdfError = `Print PDF generation error: ${error.message}`;
            console.error(printPdfError);
        }

        // Generate cover PDF
        try {
            console.log("Generating cover PDF...");
            const coverPdfResponse = await fetch(`${process.env.REACT_APP_BACKEND}/user/generateCoverPdf`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: idToken,
                },
                body: JSON.stringify({ 
                    bookId: storyId,
                    coverImageNum: selectedCoverImage
                })
            });

            if (coverPdfResponse.ok) {
                setCoverPdfGenerated(true);
                coverPdfSuccess = true;
                console.log("Cover PDF generated successfully");
            } else {
                const errorText = await coverPdfResponse.text();
                coverPdfError = `Cover PDF generation failed: ${errorText}`;
                console.error(coverPdfError);
            }
        } catch (error) {
            coverPdfError = `Cover PDF generation error: ${error.message}`;
            console.error(coverPdfError);
        }

        // Handle results based on what succeeded/failed
        if (printPdfSuccess && coverPdfSuccess) {
            setCoverImageConfirmed(true);
            showModal('success', 'Files Generated', 'Both PDF files have been generated successfully! Please download and review them, then click Submit Print Order.');
        } else if (printPdfSuccess && !coverPdfSuccess) {
            setCoverImageConfirmed(true);
            showModal('warning', 'Partial Success', `Print PDF generated successfully, but cover PDF failed: ${coverPdfError}. You can retry generating the cover PDF.`);
        } else if (!printPdfSuccess && coverPdfSuccess) {
            setCoverImageConfirmed(true);
            showModal('warning', 'Partial Success', `Cover PDF generated successfully, but print PDF failed: ${printPdfError}. You can retry generating the print PDF.`);
        } else {
            // Both failed
            setCoverImageConfirmed(true);
            const combinedError = [printPdfError, coverPdfError].filter(Boolean).join(' | ');
            showModal('error', 'PDF Generation Failed', `Both PDF files failed to generate: ${combinedError}`);
        }
    } catch (error) {
        console.error("Error in cover selection and PDF generation:", error);
        showModal('error', 'Process Failed', `Failed to prepare files: ${error.message}`);
    } finally {
        setGeneratingPDFs(false);
    }
};

const retryPrintPdf = async () => {
    try {
        setGeneratingPDFs(true);
        const idToken = await user.getIdToken();
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND}/user/getPrintPdf`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: idToken,
            },
            body: JSON.stringify({ bookId: storyId })
        });

        if (response.ok) {
            setPrintBookPdfGenerated(true);
            showModal('success', 'Print PDF Generated', 'Print PDF has been generated successfully!');
        } else {
            throw new Error("Failed to generate print PDF");
        }
    } catch (error) {
        console.error("Error retrying print PDF:", error);
        showModal('error', 'Retry Failed', `Failed to generate print PDF: ${error.message}`);
    } finally {
        setGeneratingPDFs(false);
    }
};

const retryCoverPdf = async () => {
    try {
        setGeneratingCoverPDF(true);
        const idToken = await user.getIdToken();
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND}/user/generateCoverPdf`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: idToken,
            },
            body: JSON.stringify({ 
                bookId: storyId,
                coverImageNum: selectedCoverImage
            })
        });

        if (response.ok) {
            setCoverPdfGenerated(true);
            showModal('success', 'Cover PDF Generated', 'Cover PDF has been generated successfully!');
        } else {
            throw new Error("Failed to generate cover PDF");
        }
    } catch (error) {
        console.error("Error retrying cover PDF:", error);
        showModal('error', 'Retry Failed', `Failed to generate cover PDF: ${error.message}`);
    } finally {
        setGeneratingCoverPDF(false);
    }
};

// Check PDF statuses (keep existing implementation)
    const checkPdfStatuses = async (storyID) => {
        try {
            const idToken = await user.getIdToken();
            
            const [coverResponse, printResponse] = await Promise.all([
                fetch(`${process.env.REACT_APP_BACKEND}/user/checkCoverPdfStatus`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: idToken,
                    },
                    body: JSON.stringify({ bookId: storyID })
                }),
                fetch(`${process.env.REACT_APP_BACKEND}/user/checkInnerFileStatus`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: idToken,
                    },
                    body: JSON.stringify({ bookId: storyID })
                })
            ]);
            
            if (coverResponse.ok && printResponse.ok) {
                const [coverStatus, printStatus] = await Promise.all([
                    coverResponse.json(),
                    printResponse.json()
                ]);
                console.log("PDF Status Check Results:", { coverStatus, printStatus }); // ADD THIS
                if (coverStatus.exists) {
                    setCoverPdfGenerated(true);
                    console.log("Cover PDF exists, setting state to true");
                }
                if (printStatus.exists) {
                    setPrintBookPdfGenerated(true);
                    console.log("Print PDF exists, setting state to true");
                }
            }
        } catch (error) {
            console.error("Error checking PDF statuses:", error);
        }
    };

    // check order status
const checkOrderStatus = async () => {
    try {
        console.log('Testing check order status...');
        const idToken = await user.getIdToken();
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND}/user/print/check-order-status`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: idToken,
            },
            body: JSON.stringify({ 
                orderId: orderId
            })
        });

        if (response.ok) {
            const result = await response.json();
            console.log("Check order status result:", result);
            showModal('success', 'Order Status Check', 
                `Status: ${result.status || 'Unknown'}\n` +
                `Order ID: ${result.order_id || 'Not available'}\n` +
                `Tracking: ${result.tracking ? 'Available' : 'Not available'}`
            );
        } else {
            const errorData = await response.json();
            console.error("Check order status failed:", errorData);
            showModal('error', 'Status Check Failed', `Error: ${errorData.error || 'Unknown error'}`);
        }
    } catch (error) {
        console.error("Error checking order status:", error);
        showModal('error', 'Request Failed', `Failed to check order status: ${error.message}`);
    }
};

//for checking status by admin
{/*const checkAdminOrderStatus = async () => {
    try {
        console.log('Testing check order status...');
        const idToken = await user.getIdToken();
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND}/user/print/admin-check-order-status`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: idToken,
            },
            body: JSON.stringify({ 
                orderId: orderId
            })
        });

        if (response.ok) {
            const result = await response.json();
            console.log("Check order status result:", result);
            showModal('success', 'Order Status Check', 
                `Status: ${result.status || 'Unknown'}\n` +
                `Order ID: ${result.order_id || 'Not available'}\n` +
                `Tracking: ${result.tracking ? 'Available' : 'Not available'}`
            );
        } else {
            const errorData = await response.json();
            console.error("Check order status failed:", errorData);
            showModal('error', 'Status Check Failed', `Error: ${errorData.error || 'Unknown error'}`);
        }
    } catch (error) {
        console.error("Error checking order status:", error);
        showModal('error', 'Request Failed', `Failed to check order status: ${error.message}`);
    }
};*/}

// Function to check print order status
const checkPrintOrderStatus = async () => {
    try {
        const idToken = await user.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_BACKEND}/user/getOrderStatus`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: idToken,
            },
            body: JSON.stringify({ 
                storyId: storyId,
                orderId: orderId
            })
        });

        if (response.ok) {
            const result = await response.json();
            console.log("Print order status result:", result); // Add logging
            if (result.orderStatus === 'submitted') {
                setSubmissionSuccessful(true);
            }
        } else {
            console.log("Failed to get order status:", response.status);
        }
    } catch (error) {
        console.error("Error checking print order status:", error);
    }
};

// Function to re-download print book PDF
    const downloadPrintBookPdf = async () => {
        try {
            setGeneratingPDFs(true);
            const idToken = await user.getIdToken();
            
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND}/user/getPrintPdf`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: idToken,
                    },
                    body: JSON.stringify({ 
                        bookId: storyId
                    }),
                }
            );

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${storyTitle}-PrintBook.pdf`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            }
        } catch (error) {
            console.error("Error downloading print PDF:", error);
            //alert("Failed to download print PDF. Please try again.");
            showModal('error', 'Download Failed', 'Failed to download print PDF. Please try again.');
        } finally {
            setGeneratingPDFs(false);
        }
    };

    // Function to re-download cover PDF
const downloadCoverPdf = async () => {
    try {
        setGeneratingCoverPDF(true);
        const idToken = await user.getIdToken();
        
        const response = await fetch(
            `${process.env.REACT_APP_BACKEND}/user/downloadCoverPdf`, // ✅ Changed endpoint
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: idToken,
                },
                body: JSON.stringify({ 
                    bookId: storyId
                    // ✅ No need for coverImageNum since we're just downloading existing PDF
                }),
            }
        );

        if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${storyTitle}-Cover.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            console.log("Existing cover PDF downloaded successfully");
        } else {
            const errorText = await response.text();
            console.error("downloadCoverPdf error:", errorText);
            throw new Error("Failed to download cover PDF");
        }
    } catch (error) {
        console.error("Error downloading cover PDF:", error);
        //alert("Failed to download cover PDF. Please try again.");
        showModal('error', 'Download Failed', 'Failed to download cover PDF. Please try again.');
    } finally {
        setGeneratingCoverPDF(false);
    }
};
 

    const submitToPrint = async () => {
        try {
            setSubmittingToPrint(true);
            const idToken = await user.getIdToken();
            
            const response = await fetch(`${process.env.REACT_APP_BACKEND}/user/print/submit-job`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: idToken,
                },
                body: JSON.stringify({ 
                    storyId: storyId,
                    orderId: orderId
                })
            });
    
            if (response.ok) {
                const result = await response.json();
                console.log("Print job submitted:", result);
                // Check if success is true
                if (result.success === true) {
                    setSubmissionSuccessful(true); // Update state to hide submit button
                    //showModal('success', 'Print Job Submitted', `🎉  You'll receive tracking details via email once your book ships.`);
                    showModal('success', 'Print Job Submitted', `🎉 ${result.message}`);
                   // alert(`🎉 ${result.message}`);
                } else {
                    showModal('error', 'Submission Failed', 'Print job submission was not successful');
                   // alert("Print job submission was not successful");
                }
            } else {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to submit print job");
            }
        } catch (error) {
            console.error("Error submitting print job:", error);
            //alert(`Failed to submit print job: ${error.message}`);
            showModal('error', 'Submission Failed', `Failed to submit print job: ${error.message}`);
        } finally {
            setSubmittingToPrint(false);
        }
    };
    
    // Add this new function to check user submission status
const checkUserSubmissionStatus = async () => {
    try {
        const idToken = await user.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_BACKEND}/user/getUserSubmittedStatus`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: idToken,
            },
            body: JSON.stringify({ 
                storyId: storyId,
                orderId: orderId
            })
        });

        if (response.ok) {
            const result = await response.json();
            console.log("User submission status result:", result);
            if (result.user_submitted === 1) {
                setUserSubmissionSuccessful(true);
                return true;
            }
        } else if (response.status === 404) {
            // user_submitted is null or 0, order not yet submitted
            console.log("Order not yet submitted by user");
            return false;
        } else {
            console.log("Failed to get user submission status:", response.status);
        }
        return false;
    } catch (error) {
        console.error("Error checking user submission status:", error);
        return false;
    }
};

    // Initial data fetching
    useEffect(() => {
        if (user) {
            console.log("User detected, fetching user data...");
            fetchUserName();
            
            // Start checking cover image status and fetching images
            if (storyId) {
                console.log("Checking cover status and fetching images for storyId:", storyId);
                // Always fetch image numbers first (needed for PDF generation later)
                //fetchImageNumbers(storyId);
                // Check PDF statuses first
                 // Check all statuses in parallel
                Promise.all([
                    checkPdfStatuses(storyId),
                   // checkPrintOrderStatus(),
                    checkUserSubmissionStatus()
                ]).then(([_, isSubmitted]) => {
                    // After checking submission status, handle cover image logic
                    checkCoverImageStatus(storyId).then(isAlreadySet => {
                        if (!isAlreadySet && !isSubmitted) {
                            console.log("Cover not set and order not submitted, fetching all images");
                            fetchStoryImages(storyId);
                        } else {
                            console.log("Cover already set or order already submitted");
                        }
                    });
                });
            }
        }
    }, [user, storyId]);

    // Clean up object URLs when component unmounts
    useEffect(() => {
        return () => {
            // Clean up object URLs to prevent memory leaks
            storyImages.forEach(imageUrl => {
                if (imageUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(imageUrl);
                }
            });
        };
    }, [storyImages]);

    // Show loading or error if missing required data
    if (!storyId) {
        return (
            <div>
                <PageNav className="absolute inset-x-0 top-0 z-50" />
                <div className="flex justify-center p-4">
                    <div className="text-center">
                        <p className="text-red-600">Missing story information. Please try again.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <PageNav className="absolute inset-x-0 top-0 z-50" />
               {/* Modal Component */}
               <Modal
                isOpen={modal.isOpen}
                onClose={closeModal}
                type={modal.type}
                title={modal.title}
                message={modal.message}
            />
            <div className="flex justify-center text-lg font-semibold p-4 box mt-3 text-center text-indigo-600">
                Welcome <br />
                {userName || (user ? (user.displayName ? user.displayName : user.email) : "Guest")}
            </div>

             {/* Conditionally render different content based on submission status */}
        { (userSubmissionSuccessful || submissionSuccessful) ? (
            // Already submitted - show confirmation and files only
            <>
                <div className="flex justify-center bg-white px-6 py-10 lg:px-8">
                    <div role="list" className="mt-8 max-w-2xl space-y-8 text-gray-600 text-justify">
                        <div className="flex gap-x-3">
                            <CheckCircleIcon className="mt-1 h-5 w-10 flex-none text-green-600" aria-hidden="true" />
                            <span>
                                <strong className="font-semibold text-gray-900">Order Submitted Successfully: </strong>
                                Your print order for "{storyTitle}" (Order #{orderId}) has been submitted for processing. 
                                You can download your files below while we prepare your book for printing.
                            </span>
                        </div>
                    </div>
                </div>

                {/* Show selected cover image and download options for submitted orders */}
                {imageData.length > 0 && (
                    <div className="flex justify-center p-4">
                        <div className="max-w-4xl">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
                                Your Selected Cover Image
                            </h3>
                            
                            <div className="flex justify-center mb-6">
                                <div className="border rounded-lg overflow-hidden shadow-sm max-w-sm">
                                    <img
                                        src={imageData[0].imageUrl}
                                        alt="Selected cover image"
                                        className="w-full h-64 object-cover"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                    <div className="p-3 text-center">
                                        <p className="text-sm font-semibold text-green-600">
                                            ✓ Selected Cover
                                        </p>
                                     {/*}   <p className="text-xs text-gray-500 mt-1">
                                            Image #{imageData[0].imageNum}
                                        </p> */}
                                    </div>
                                </div>
                            </div>

                            {/* Download section for submitted orders */}
                           
                                <div className="text-center">
                                    <div className="p-6 bg-green-50 rounded-lg border border-green-200">
                                        <h4 className="text-lg font-semibold text-green-800 mb-4">
                                            Your Print Files
                                        </h4>
                                        
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                            <p className="text-sm text-blue-800 mb-3">
                                                Download your print files:
                                            </p>
                                            <div className="flex gap-4 justify-center">
                                                <button
                                                    onClick={downloadPrintBookPdf}
                                                    className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                                                >
                                                    <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                                                    Download Interior
                                                </button>
                                                <button
                                                    onClick={downloadCoverPdf}
                                                    className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                                                >
                                                    <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                                                    Download Cover
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                           

                            {/* Final confirmation for submitted orders */}
                            <div className="text-center mt-6">
                                <div className="p-6 bg-green-50 rounded-lg border border-green-200">
                                    <CheckCircleIcon className="h-16 w-16 text-green-600 mx-auto mb-4" />
                                    <h4 className="text-xl font-semibold text-green-800 mb-2">
                                        Order Submitted Successfully!
                                    </h4>
                                    <p className="text-sm text-green-600">
                                        Order #{orderId}
                                    </p>
                                </div>
                            </div>
                            {/* Test button - add this where you want it to appear */}

  {/*}  <div className="text-center mt-4">
        <button
            onClick={checkOrderStatus}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
        >
            Check Order Status
        </button>
    </div> */}
                        </div>
                    </div>
                )}
            </>
        ) : (
            // Not yet submitted - show original flow
            <>

            <div className="flex justify-center bg-white px-6 py-10 lg:px-8">
                <div role="list" className="mt-8 max-w-2xl space-y-8 text-gray-600 text-justify">
                    <div className="flex gap-x-3">
                        <CheckBadgeIcon className="mt-1 h-5 w-10 flex-none text-indigo-600" aria-hidden="true" />
                        <span>
                            <strong className="font-semibold text-gray-900">Payment Received Successfully: </strong>
                            Thank you for your order! We have received your payment for "{storyTitle}" (Order #{orderId}). 
                            Please select your cover image and we'll prepare your book for printing.
                        </span>
                    </div>
                </div>
            </div>

            {/* Display story images */}
            {loadingImages && (
                <div className="flex justify-center p-4">
                    <p className="text-gray-600">Loading story images...</p>
                </div>
            )}

            {imageData.length > 0 && (
                <div className="flex justify-center p-4">
                    <div className="max-w-4xl">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
                            {coverImageConfirmed ? "Your Selected Cover Image" : "Choose Your Cover Image"}
                        </h3>

                        {/* If cover is confirmed, show single centered image */}
                        {coverImageConfirmed ? (
                            <div className="flex justify-center">
                                <div className="border rounded-lg overflow-hidden shadow-sm max-w-sm">
                                    <img
                                        src={imageData[0].imageUrl}
                                        alt="Selected cover image"
                                        className="w-full h-64 object-cover"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                    <div className="p-3 text-center">
                                        <p className="text-sm font-semibold text-green-600">
                                            ✓ Selected Cover
                                        </p>
                                      {/*}  <p className="text-xs text-gray-500 mt-1">
                                            Image #{imageData[0].imageNum}
                                        </p> */}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* If cover NOT confirmed, show selection interface */
                            <>
                                {/* Mobile: Horizontal scroll */}
                                <div className="block sm:hidden">
                                    <div className="flex overflow-x-auto space-x-4 pb-4">
                                        {imageData.map((item, index) => (
                                            <div key={index} className="flex-shrink-0 w-64 border rounded-lg overflow-hidden shadow-sm">
                                                <img
                                                    src={item.imageUrl}
                                                    alt={`Story image ${index + 1}`}
                                                    className="w-full h-48 object-cover"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                                <div className="p-3 text-center">
                                                    <label className="flex items-center justify-center cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="coverImage"
                                                            value={item.imageNum}
                                                            checked={selectedCoverImage === item.imageNum}
                                                            onChange={() => handleCoverImageSelect(item)}
                                                            className="mr-2 text-indigo-600 focus:ring-indigo-500"
                                                        />
                                                        <span className="text-sm text-gray-700">
                                                            Select as Cover
                                                        </span>
                                                    </label>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Image #{item.imageNum}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Desktop: Grid */}
                                <div className="hidden sm:grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                    {imageData.map((item, index) => (
                                        <div key={index} className="border rounded-lg overflow-hidden shadow-sm">
                                            <img
                                                src={item.imageUrl}
                                                alt={`Story image ${index + 1}`}
                                                className="w-full h-32 object-cover"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                            <div className="p-3 text-center">
                                                <label className="flex items-center justify-center cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="coverImage"
                                                        value={item.imageNum}
                                                        checked={selectedCoverImage === item.imageNum}
                                                        onChange={() => handleCoverImageSelect(item)}
                                                        className="mr-2 text-indigo-600 focus:ring-indigo-500"
                                                    />
                                                    <span className="text-sm text-gray-700">
                                                        Select as Cover
                                                    </span>
                                                </label>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Image #{item.imageNum}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                       {/* Confirm selection button */}
                        {selectedCoverImage && !coverImageConfirmed && (
                             <div className="text-center">
                             <button
                                 onClick={submitCoverImageSelection}
                                 disabled={generatingPDFs}
                                 className="bg-indigo-600 text-white px-8 py-3 rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 mt-6"
                             >
                                 {generatingPDFs ? (
                                     
    
                                         'Generating Files...'
                                     
                                        ) : ( 
                                     'Confirm Cover & Generate Files'
                                     )}
                             </button>
                              {/* Time estimate notice */}
                            {generatingPDFs && (
                                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div className="flex items-center justify-center text-blue-800">
                                        <InformationCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                                        <p className="text-sm">
                                            Please wait while we generate your print files. This process typically takes 3-5 minutes.
                                        </p>
                                    </div>
                                </div>
                            )}
                         </div>
                     )}

                     {/* Add this section after the cover image confirmation and before the "files ready" section */}
{coverImageConfirmed && (
    <>
        {/* Partial success states - show retry buttons */}
        {(printBookPdfGenerated && !coverPdfGenerated) && (
            <div className="text-center mt-6">
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                    <h4 className="text-lg font-semibold text-yellow-800 mb-2">
                        Cover PDF Generation Failed
                    </h4>
                    <p className="text-sm text-yellow-700 mb-4">
                        Your print book PDF was generated successfully, but the cover PDF failed. 
                        Please retry generating the cover PDF.
                    </p>
                    <button
                        onClick={retryCoverPdf}
                        disabled={generatingCoverPDF}
                        className="bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700 focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 disabled:opacity-50"
                    >
                        {generatingCoverPDF ? (
                            <>
                                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Retrying Cover PDF...
                            </>
                        ) : (
                            'Retry Cover PDF'
                        )}
                    </button>
                </div>
            </div>
        )}

        {(!printBookPdfGenerated && coverPdfGenerated) && (
            <div className="text-center mt-6">
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                    <h4 className="text-lg font-semibold text-yellow-800 mb-2">
                        Print PDF Generation Failed
                    </h4>
                    <p className="text-sm text-yellow-700 mb-4">
                        Your cover PDF was generated successfully, but the print book PDF failed. 
                        Please retry generating the print PDF.
                    </p>
                    <button
                        onClick={retryPrintPdf}
                        disabled={generatingPDFs}
                        className="bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700 focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 disabled:opacity-50"
                    >
                        {generatingPDFs ? (
                            <>
                                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Retrying Print PDF...
                            </>
                        ) : (
                            'Retry Print PDF'
                        )}
                    </button>
                </div>
            </div>
        )}

        {(!printBookPdfGenerated && !coverPdfGenerated) && (
            <div className="text-center mt-6">
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <XCircleIcon className="h-8 w-8 text-red-600 mx-auto mb-2" />
                    <h4 className="text-lg font-semibold text-red-800 mb-2">
                        PDF Generation Failed
                    </h4>
                    <p className="text-sm text-red-700 mb-4">
                        Both PDF files failed to generate. Please retry both operations.
                    </p>
                    <div className="flex gap-4 justify-center">
                        <button
                            onClick={retryPrintPdf}
                            disabled={generatingPDFs}
                            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                        >
                            {generatingPDFs ? 'Retrying...' : 'Retry Print PDF'}
                        </button>
                        <button
                            onClick={retryCoverPdf}
                            disabled={generatingCoverPDF}
                            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                        >
                            {generatingCoverPDF ? 'Retrying...' : 'Retry Cover PDF'}
                        </button>
                    </div>
                </div>
            </div>
        )}
    </>
)}
                      {/* File review and submission section */}
                      {coverImageConfirmed && printBookPdfGenerated && coverPdfGenerated && (
                            <div className="text-center">
                                <div className="p-6 bg-green-50 rounded-lg border border-green-200">
                                    <h4 className="text-lg font-semibold text-green-800 mb-4">
                                        Your Print Files Are Ready!
                                    </h4>
                                    
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                        <p className="text-sm text-blue-800 mb-3">
                                            Review your files before submitting to print:
                                        </p>
                                        <div className="flex gap-4 justify-center">
                                            <button
                                                onClick={downloadPrintBookPdf}
                                                className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                                            >
                                                <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                                                Download Interior
                                            </button>
                                            <button
                                                onClick={downloadCoverPdf}
                                                className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                                            >
                                                <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                                                Download Cover
                                            </button>
                                        </div>
                                    </div>

                                    <button
                                        onClick={submitToPrint}
                                        disabled={submittingToPrint}
                                        className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 font-medium"
                                    >
                                    <PrinterIcon className="inline h-5 w-5 mr-2" />
                                    Submit Print Order
                                    </button>   
                                </div>
                            </div>
                        )}

                        {/* Order submitted confirmation */}
                        {submissionSuccessful && (
                            <div className="text-center">
                                <div className="p-6 bg-green-50 rounded-lg border border-green-200">
                                    <CheckCircleIcon className="h-16 w-16 text-green-600 mx-auto mb-4" />
                                    <h4 className="text-xl font-semibold text-green-800 mb-2">
                                        Order Submitted Successfully!
                                    </h4>
                                    <p className="text-sm text-green-600">
                                        Order #{orderId}
                                    </p>
                                </div>
                            </div>
                        )}

                            
    
                    </div>
                </div>
            )}

           
   {/* Enhanced debug info */}
   {!loadingImages && imageData.length === 0 && (
                <div className="flex justify-center p-4">
                    <div className="text-center">
                        <p className="text-red-600">No images found for this story.</p>
                        <p className="text-sm text-gray-500 mt-2">
                            Story ID: {storyId || 'Not found'}
                        </p>
                        <p className="text-sm text-gray-500">
                            Check console for detailed error logs.
                        </p>
                    </div>
                </div>
            )}
             </>
        )}
        </div>
    );
}